import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, Link2, Mail, Palette, CheckCircle2, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrganizationAdmin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    custom_id: '',
    organization_name: '',
    contact_email: '',
    subscription_tier: 'free',
    logo_url: '',
    primary_color: '#10b981'
  });
  const [createdOrg, setCreatedOrg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const orgData = {
        custom_id: formData.custom_id.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        organization_name: formData.organization_name,
        contact_email: formData.contact_email,
        subscription_tier: formData.subscription_tier,
        branding: {
          organization_name: formData.organization_name,
          logo_url: formData.logo_url || null,
          primary_color: formData.primary_color
        }
      };

      const response = await axios.post(`${API}/organizations`, orgData);
      setCreatedOrg(response.data);
      toast.success('Organization created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const customUrl = createdOrg ? `${window.location.origin}/org/${createdOrg.custom_id}` : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_health-cost-finder/artifacts/kj0ydf6o_HCD-1.png"
              alt="Healthcare Dollar Logo"
              className="h-12 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Organization Registration</h1>
          <p className="text-gray-600">Create a custom branded cost estimation page for your healthcare facility</p>
        </div>

        {!createdOrg ? (
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-6 h-6" />
                Register Your Organization
              </CardTitle>
              <CardDescription className="text-green-100">
                Get a custom URL and branded page to share with your patients
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    type="text"
                    placeholder="Stanford Health Center"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom_id">Custom URL ID *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">/org/</span>
                    <Input
                      id="custom_id"
                      type="text"
                      placeholder="stanford-health"
                      value={formData.custom_id}
                      onChange={(e) => setFormData({...formData, custom_id: e.target.value})}
                      required
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500">URL-friendly ID (lowercase, hyphens allowed)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="info@stanfordhealth.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subscription_tier">Subscription Tier *</Label>
                  <select
                    id="subscription_tier"
                    value={formData.subscription_tier}
                    onChange={(e) => setFormData({...formData, subscription_tier: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="free">Free (with PDF payment)</option>
                    <option value="basic">Basic ($99/month - Free PDFs for patients)</option>
                    <option value="premium">Premium ($299/month - Free PDFs + Analytics)</option>
                  </select>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Branding Customization
                  </h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="logo_url">Logo URL (optional)</Label>
                      <Input
                        id="logo_url"
                        type="url"
                        placeholder="https://yoursite.com/logo.png"
                        value={formData.logo_url}
                        onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                      />
                      <p className="text-xs text-gray-500">Direct URL to your organization's logo</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="primary_color">Primary Brand Color</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="primary_color"
                          type="color"
                          value={formData.primary_color}
                          onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                          className="w-20 h-12"
                        />
                        <Input
                          type="text"
                          value={formData.primary_color}
                          onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                          placeholder="#10b981"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create Organization'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-xl">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6" />
                Organization Created Successfully!
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-8 space-y-6">
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <h3 className="font-semibold text-green-800 mb-2">Your Custom URL</h3>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-4 py-2 rounded text-sm text-gray-800 border">
                    {customUrl}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(customUrl)}
                    variant="outline"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-800">Organization Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Organization Name:</p>
                    <p className="font-semibold">{createdOrg.organization_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Custom ID:</p>
                    <p className="font-semibold">{createdOrg.custom_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Contact Email:</p>
                    <p className="font-semibold">{createdOrg.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Subscription Tier:</p>
                    <p className="font-semibold capitalize">{createdOrg.subscription_tier}</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => window.open(customUrl, '_blank')}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  View Page
                </Button>
                <Button
                  onClick={() => {
                    setCreatedOrg(null);
                    setFormData({
                      custom_id: '',
                      organization_name: '',
                      contact_email: '',
                      subscription_tier: 'free',
                      logo_url: '',
                      primary_color: '#10b981'
                    });
                  }}
                  variant="outline"
                >
                  Create Another
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
